import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

const HomePortfolio = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [showLogo, setShowLogo] = useState(true);
  const [imageLoaded, setImageLoaded] = useState({});
  const [featuredPortfolio, setFeaturedPortfolio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Tab headings
  const tabHeadings = ['Website Development', 'Mobile Application', 'Graphic Designs'];
  const tabHeadingsmobile = ['Websites', 'Applications', 'Graphics'];

  // Category mapping
  const categoryMap = {
    'website': 0,
    'mobileApp': 1,
    'graphic': 2,
    'digital': 0
  };

  // Fetch featured portfolio from API
  useEffect(() => {
    fetchFeaturedPortfolio();
  }, []);

  const fetchFeaturedPortfolio = async () => {
    try {
      const res = await axios.get("https://dataap.werkeon.com/api/get_portfolio.php");
      
      const featuredItems = res.data.filter(item => item.featured);
      
      const groupedByCategory = {
        0: [],
        1: [],
        2: []
      };

      featuredItems.forEach(item => {
        const tabIndex = categoryMap[item.service_id] || 0;
        if (groupedByCategory[tabIndex].length < 6) {
          groupedByCategory[tabIndex].push({
            link: '/showcase',
            imgSrc: item.image,
            alt: item.title,
            id: item.id,
            title: item.title,
            category: item.service_id
          });
        }
      });

      setFeaturedPortfolio(groupedByCategory);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching featured portfolio:", err);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (featuredPortfolio[activeTab] && featuredPortfolio[activeTab].length > 0) {
      setShowLogo(true);
      const timer = setTimeout(() => {
        setShowLogo(false);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [activeTab, featuredPortfolio]);

  const handleImageLoad = (imageId) => {
    setImageLoaded(prev => ({
      ...prev,
      [imageId]: true
    }));
  };

  // Open modal with selected image
  const openModal = (imageData) => {
    setSelectedImage(imageData);
    setModalOpen(true);
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
  };

  // Close modal
  const closeModal = () => {
    setModalOpen(false);
    setSelectedImage(null);
    // Restore body scroll
    document.body.style.overflow = 'unset';
  };

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    };

    if (modalOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [modalOpen]);

  // Preload images for better performance
  useEffect(() => {
    if (featuredPortfolio[activeTab]) {
      featuredPortfolio[activeTab].forEach(item => {
        const img = new Image();
        img.src = item.imgSrc;
      });
    }
  }, [featuredPortfolio, activeTab]);

  if (loading) {
    return (
      <section className="p-4 md:p-8">
        <div className="text-center">
          <h2 className="my-4 font-bold text-2xl max-w-[492px] m-auto sm:text-4xl border-b-2 border-[#E12126] uppercase">
            Creative Space <span className="text-[#21AA4A]">Showcase</span>
          </h2>
        </div>
        <div className="flex justify-center items-center min-h-[300px]">
          <div className="text-center">
            <div className="loading-breathing">
              <img
                src="../images/logos/header-logo.png"
                alt="Website Logo"
                className="logo-breathing w-20 h-20 mx-auto"
              />
            </div>
            <p className="mt-4 text-gray-600">Loading featured projects...</p>
          </div>
        </div>
      </section>
    );
  }

  const currentTabItems = featuredPortfolio[activeTab] || [];

  return (
    <>
      <section className="p-4 md:p-8">
        <div className="text-center">
          <h2 className="my-4 font-bold text-2xl max-w-[492px] m-auto sm:text-4xl border-b-2 border-[#E12126] uppercase">
            Creative Space <span className="text-[#21AA4A]">Showcase</span>
          </h2>
          <p className="text-gray-600 mt-2 text-sm md:text-base">
            Featured projects that showcase our best work
          </p>
        </div>
        
        <div className='flex mt-0 md:mt-16 justify-center'>
          <div className="block md:flex w-5/5 sm:w-[90%] md:w-4/5 max-w-8xl">
            {/* Left Column with Tab Headings */}
            <div className="grid grid-cols-3 md:flex md:flex-col justify-center items-center md:items-start w-full md:w-1/3 p-2 gap-2 md:gap-4">
              {tabHeadings.map((heading, index) => (
                <div
                  key={index}
                  className={`p-3 border-b w-full cursor-pointer text-left transition-colors duration-300 ease-in-out hidden md:block ${
                    activeTab === index ? 'text-black border-b-2 border-[#21AA4A]' : 'text-gray-600'
                  }`}
                  onMouseEnter={() => setActiveTab(index)}
                >
                  <div className='flex gap-4 align-center'>
                    <strong className='uppercase hidden sm:block'>Werkeon<br></br> Presents</strong>
                    <p className='flex items-center content-center text-[12px] sm:text-[14px] md:text-[16px] hidden md:block'>
                      {heading} 
                      {featuredPortfolio[index] && (
                        <span className="ml-2 text-xs text-green-500">
                          ({featuredPortfolio[index].length})
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              ))}
              
              {tabHeadingsmobile.map((heading, index) => (
                <div
                  key={index}
                  className={`p-3 border-b w-full cursor-pointer text-left transition-colors duration-300 ease-in-out block md:hidden ${
                    activeTab === index ? 'text-black border-b-2 border-[#21AA4A]' : 'text-gray-600'
                  }`}
                  onMouseEnter={() => setActiveTab(index)}
                >
                  <div className='flex gap-4 align-center justify-center'>
                    <p className='flex items-center text-[12px] sm:text-[16px] block md:hidden'>
                      {heading}
                      {featuredPortfolio[index] && (
                        <span className="ml-1 text-xs text-green-500">
                          ({featuredPortfolio[index].length})
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Right Column with Featured Images */}
            <div className="flex-1 p-4 min-h-[150px] sm:min-h-[280px] md:min-h-[200px]">
              <div className="z-10 grow relative aspect-auto">
                {currentTabItems.length > 0 ? (
                  <ul className="flex items-center mx-auto relative w-full max-w-[380px] sm:max-w-full md:w-[90%]">
                    {currentTabItems.map((edition, index) => (
                      <li
                        key={edition.id}
                        className="absolute top-0 overflow-hidden rounded-lg shadow-cutting-edge-edition w-1/2 transition-all duration-500 ease-in-out hover:transform hover:scale-x-[1.3] hover:scale-y-[1.5] hover:rotate-0 hover:z-20 hover:translate-y-[-10px] custom-perspective cursor-pointer"
                        style={{
                          left: `calc(${(index / currentTabItems.length) * 100}%)`,
                        }}
                        onClick={() => openModal(edition)}
                      >
                        <div className="h-[150px] md:h-[250px] overflow-hidden bg-gray-100">
                          {showLogo && (
                            <div className="loading-breathing h-full flex items-center justify-center">
                              <img
                                src="../images/logos/header-logo.png"
                                alt="Website Logo"
                                className="logo-breathing w-12 h-12 opacity-70"
                              />
                            </div>
                          )}
                          {!showLogo && (
                            <div className="relative w-full h-full">
                              <img
                                src={edition.imgSrc}
                                alt={edition.alt}
                                loading="lazy"
                                decoding="async"
                                className={`w-full h-full object-cover transition-opacity duration-300 ${
                                  imageLoaded[edition.id] ? 'opacity-100' : 'opacity-0'
                                }`}
                                onLoad={() => handleImageLoad(edition.id)}
                                style={{
                                  objectPosition: 'center top'
                                }}
                              />
                              {/* Low-quality placeholder */}
                              {!imageLoaded[edition.id] && (
                                <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
                                  <div className="w-8 h-8 border-2 border-gray-300 border-t-green-500 rounded-full animate-spin"></div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No featured projects in this category.</p>
                    <p className="text-sm text-gray-400 mt-2">
                      Mark some projects as featured in your portfolio management.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <Link
          className="flex justify-center m-auto max-w-[110px] mt-[3rem] md:mt-[4rem] text-[10px] md:text-[14px] items-center uppercase justify-center rounded-xl bg-[#21AA4A] px-3 py-2 font-semibold text-white shadow-sm transition-all duration-150 hover:bg-[#175293] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
          to="/showcase"
        >
          See More
        </Link>
      </section>

      {/* Modal for Full Image View */}
      {modalOpen && selectedImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4 transition-opacity duration-300"
          onClick={closeModal}
        >
          <div 
            className="relative max-w-7xl w-full max-h-[95vh] bg-transparent rounded-lg"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              className="absolute top-4 right-4 z-10 text-white hover:text-gray-300 transition-colors duration-200 bg-black bg-opacity-50 rounded-full p-2"
              onClick={closeModal}
              aria-label="Close modal"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Image Container */}
            <div className="flex flex-col items-center justify-center h-full">
              {/* Image Title */}
              <div className="text-center mb-4">
                <h3 className="text-xl font-semibold text-white">{selectedImage.title}</h3>
                <p className="text-gray-300 capitalize">{selectedImage.category}</p>
              </div>

              {/* Full Image */}
              <div className="relative w-full h-[80vh] flex items-center justify-center">
                <img
                  src={selectedImage.imgSrc}
                  alt={selectedImage.alt}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                  loading="eager"
                />
                
                {/* Loading Spinner for Modal Image */}
                {!imageLoaded[selectedImage.id] && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-12 border-4 border-gray-300 border-t-white rounded-full animate-spin"></div>
                  </div>
                )}
              </div>

              {/* Navigation Instructions */}
              <div className="text-center mt-4 text-gray-400 text-sm">
                <p>Click outside the image or press ESC to close</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HomePortfolio;